import { OptionType } from '../components/molecules/AdminFilters/Dropdown';
import {
    ClientRowData,
    ClientStagesType,
    Flow2GoalTypeProps,
    GoalTypeProps,
    IncomeSourcesType,
    InvestmentSourcesType,
    WealthGoalType
} from './types';
import { getNewPalette } from '../themes/newPalette';

const theme = getNewPalette('advice');

export const OKTA_DEVICE_TOKEN_KEY = document
    ? document?.location?.host + '-' + 'device-token'
    : 'advice-experience-device-token';

export const fontFamily = 'TT Commons Pro';

export const LOGIN = {
    MFA_CHALLENGE: 'MFA_CHALLENGE',
    MFA: 'MFA',
    SUCCESS: 'SUCCESS',
    MFA_REQUIRED: 'MFA_REQUIRED',
    OKTA: 'OKTA'
};

export const ACCESS_LEVEL_DROPDOWN_OPTIONS = (t: any) => [
    {
        value: 'advisor',
        label: t('TEXT_ADMIN_USER_FA_HEADER')
    },
    {
        value: 'admin',
        label: t('TEXT_ADMIN_USER_FA_ADMIN_HEADER')
    }
];

export const FINANCIAL_ADVISORS_ACCESS_KEYS = {
    ADVISOR_NAME: 'advisorName',
    ADVISOR_EMAIL: 'advisorEmail',
    NO_OF_CLIENTS: 'numberOfClients',
    NO_OF_GOALS: 'numberOfGoals',
    REMOVE: 'remove'
};

export const STATUSVARIANTS = {
    COMPLETE: 'complete',
    INCOMPLETE: 'incomplete'
} as const;

export const REVIEW_GOAL_EVENT_TYPES = {
    realized: 'GOAL_REALIZED',
    notRealized: 'GOAL_EXTENDED'
} as const;

export const FINANCIAL_USERS_ACCESS_KEYS = {
    USER_NAME: 'userName',
    USER_EMAIL: 'userEmail',
    TENANT_NAME: 'userTenant',
    USER_TYPE: 'userType',
    RESET_PASSWORD: 'resetPassword',
    USER_STATUS: 'userStatus',
    PLAN_TRANSFER: 'planTransfer',
    ACCOUNT_STATE: 'accountState'
};

export const ADMIN_USER_ACCESS_KEYS = {
    ADMIN_NAME: 'adminName',
    ADMIN_EMAIL: 'adminEmail',
    REMOVE: 'remove'
};
// Create client steps
export const CLIENT_PROFILE_STAGE1 = 'personalInformation';
export const CLIENT_PROFILE_STAGE2 = 'relationships';
export const CLIENT_PROFILE_STAGE3 = 'riskTolerance';
export const CLIENT_PROFILE_STAGE4 = 'fundingSources';
// Create goal types
export const RETIREMENT_GOAL = 'RETIREMENT_GOAL';
export const WEALTH_GOAL = 'WEALTH_GOAL';
export const INCOME_GOAL = 'INCOME_GOAL';

const CLIENT_JOURNEY_STAGES: {
    CLIENT_PROFILE: ClientStagesType[];
    GOALS: GoalTypeProps[];
    GOALS_FLOW2: Flow2GoalTypeProps[];
} = {
    CLIENT_PROFILE: [
        { order: 1, key: CLIENT_PROFILE_STAGE1, optional: false },
        { order: 2, key: CLIENT_PROFILE_STAGE2, optional: true },
        { order: 3, key: CLIENT_PROFILE_STAGE3, optional: false },
        { order: 4, key: CLIENT_PROFILE_STAGE4, optional: false }
    ],
    GOALS: [
        {
            order: 1,
            category: RETIREMENT_GOAL,
            heading: 'Retirement Goal',
            type: 'retirement',
            title: 'Retirement',
            disabled: false,
            backgroundColor: theme.purple[100],
            darkBackground: '#B39AF5',
            color: theme.purple[400],
            icon: 'retirement'
        },
        {
            order: 1,
            category: WEALTH_GOAL,
            type: 'buyahouse',
            title: WealthGoalType.BUY_A_HOUSE,
            heading: 'Create Buy a House Goal',
            disabled: false,
            backgroundColor: theme.berry[100],
            darkBackground: '#F589A2',
            color: theme.berry[400],
            icon: 'house'
        },
        {
            order: 3,
            category: WEALTH_GOAL,
            type: 'buyacar',
            title: WealthGoalType.BUY_A_CAR,
            heading: 'Create Buy a Car Goal',
            disabled: false,
            backgroundColor: '#FFE0D2',
            darkBackground: '#FF9665',
            color: '#BE3200',
            icon: 'car'
        },
        {
            order: 2,
            category: WEALTH_GOAL,
            type: 'payforcollege',
            title: WealthGoalType.PAY_FOR_COLLEGE,
            heading: 'Create Pay for College Goal',
            disabled: false,
            backgroundColor: '#FAE4FD',
            darkBackground: '#F0B4F9',
            color: '#A525D0',
            icon: 'college'
        },
        {
            order: 4,
            category: WEALTH_GOAL,
            type: 'custom',
            title: WealthGoalType.CUSTOM,
            heading: 'Create Custom Goal',
            disabled: false,
            backgroundColor: '#DDEAFF',
            darkBackground: '#91B9FF',
            color: '#1446E1',
            icon: 'custom'
        }
    ],
    GOALS_FLOW2: [
        {
            order: 1,
            type: 'wealth',
            heading: 'LABEL_WEALTH',
            title: 'Wealth Goal',
            disabled: false,
            backgroundColor: theme.semantic.bg.success2,
            color: theme.semantic.text.success,
            icon: 'wealth',
            drawerTitle: 'WEALTH_GOAL_CARD_HEADER'
        },
        {
            order: 2,
            title: 'Retirement Goal',
            type: 'retirement',
            heading: 'RETIREMENT',
            disabled: false,
            backgroundColor: theme.semantic.bg.secondary1,
            color: theme.semantic.leg.oneYearAgo,
            icon: 'retirement',
            drawerTitle: 'RETIREMENT_GOAL_CARD_HEADER'
        },
        {
            order: 3,
            type: 'income',
            heading: 'LABEL_INCOME',
            title: 'Income Goal',
            disabled: false,
            backgroundColor: theme.semantic.bg.info,
            color: theme.semantic.icon.info,
            icon: 'income',
            drawerTitle: 'INCOME_GOAL_CARD_HEADER'
        }
    ]
    // APPROACH: [],
    // PLAN_DETAILS: [],
    // PROPOSAL: []
};

export const CLIENT_PROFILE_STAGE_KEYS = CLIENT_JOURNEY_STAGES.CLIENT_PROFILE.sort(
    (stage1: ClientStagesType, stage2: ClientStagesType) => Number(stage1.order) - Number(stage2.order)
).map((stage) => stage.key);

export const CLIENT_PROFILE_STAGES = CLIENT_JOURNEY_STAGES.CLIENT_PROFILE.sort(
    (stage1: ClientStagesType, stage2: ClientStagesType) => Number(stage1.order) - Number(stage2.order)
);

export const RETIREMENT_CATEGORY_GOALS = CLIENT_JOURNEY_STAGES.GOALS.filter(
    (g: GoalTypeProps) => g.category === RETIREMENT_GOAL
).sort((goal1: GoalTypeProps, goal2: GoalTypeProps) => Number(goal1.order) - Number(goal2.order));

export const WEALTH_CATEGORY_GOALS = CLIENT_JOURNEY_STAGES.GOALS.filter(
    (g: GoalTypeProps) => g.category === WEALTH_GOAL
).sort((goal1: GoalTypeProps, goal2: GoalTypeProps) => Number(goal1.order) - Number(goal2.order));

export const FLOW2_CATEGORY_GOALS = CLIENT_JOURNEY_STAGES.GOALS_FLOW2.sort(
    (goal1: Flow2GoalTypeProps, goal2: Flow2GoalTypeProps) => Number(goal1.order) - Number(goal2.order)
);

export const MAX_ALLOWED_GOALS = 6;
export const MAX_ALLOWED_AGE = 99;
export const MIN_ALLOWED_AGE = 18;
export const DEFAULT_RETIREMENT_AGE = 65;
export const DEFAULT_PLANNING_AGE = 86;
export const MAX_ALLOWED_AGE_FF = 120;
export const MIN_ALLOWED_AGE_FF = 1;
export const MAX_ALLOWED_RETIREMENT_AGE = 118;
export const MAX_RETIREMENT_AGE_WARNING = 75;

export const PORTFOLIO_COMPOSITION_COLOR = {
    equityColor: [
        theme.semantic.dat.asst.Equity,
        '#1ac6bb',
        '#33ccc2',
        '#4dd3ca',
        '#66d9d1',
        '#7fded8',
        '#99e5e1',
        '#b3ece9',
        '#ccf2f0',
        '#e6f9f8'
    ],
    fixedIncomeColor: [
        '#6730e3',
        '#7745e6',
        '#8559e9',
        '#956fec',
        '#a483ee',
        '#c2acf4',
        '#d2c1f7',
        '#e1d6f9',
        '#f0ebfd'
    ],
    cashColor: [theme.semantic.dat.asst.Cash, '#649cff', '#75a7ff', '#97bdff', '#a8c7ff'],
    allPortfoliosBaseColors: [theme.semantic.dat.asst.Equity, '#6730E3', theme.semantic.dat.asst.Cash]
};

export const iwsGoalTypeMapper: any = {
    WEALTH_GOAL: 'regular',
    retirement: 'retirement', //need to confirm
    income: 'income'
};

export const getStatusOptions = (t: any, isFlow2 = false): OptionType<string | undefined>[] => [
    {
        value: undefined,
        label: t('ALL_TEXT')
    },
    {
        value: 'Incomplete',
        label: isFlow2 ? t('INCOMPLETE_DRAFT_TEXT') : t('INCOMPLETE_TEXT')
    },
    {
        value: 'Complete',
        label: t('COMPLETE_TEXT')
    }
];

export const statusOptions: OptionType<string | undefined>[] = [
    {
        value: undefined,
        label: 'All'
    },
    {
        value: 'incomplete',
        label: 'Incomplete'
    },
    {
        value: 'complete',
        label: 'Complete'
    }
];

export const getRiskProfileOptions = (t: any): OptionType<string | undefined>[] => [
    {
        value: undefined,
        label: t('ALL_TEXT')
    },
    {
        value: 'Conservative',
        label: t('CONSERVATIVE')
    },
    {
        value: 'Moderate',
        label: t('MODERATE')
    },
    {
        value: 'Aggressive',
        label: t('AGGRESSIVE')
    }
];

export const riskProfileOptions: OptionType<string | undefined>[] = [
    {
        value: undefined,
        label: 'All'
    },
    {
        value: 'Conservative',
        label: 'Conservative'
    },
    {
        value: 'Moderate',
        label: 'Moderate'
    },
    {
        value: 'Aggressive',
        label: 'Aggressive'
    }
];

export const getRiskToleranceOptions = (
    t: any,
    riskLevels?: string[],
    minMaxEquity?: Record<
        string,
        { minEquity?: number; maxEquity?: number; min?: number; max?: number; riskProfile?: string }
    >
) => {
    const toPercent = (value: number) => (Number.isInteger(value) ? value : value * 100);

    const getMatchedEquityRange = (riskLevel: string) => {
        const riskLevelLower = riskLevel.toLowerCase();

        const matchedEntry = Object.entries(minMaxEquity || {}).find(([key, value]) => {
            const normalizedKey = key.trim().toLowerCase();
            const normalizedRiskProfile = value?.riskProfile?.trim().toLowerCase();
            return normalizedKey === riskLevelLower || normalizedRiskProfile === riskLevelLower;
        });

        if (!matchedEntry) {
            return undefined;
        }

        const [, matchedEquityRange] = matchedEntry;
        const minValue = matchedEquityRange.minEquity ?? matchedEquityRange.min;
        const maxValue = matchedEquityRange.maxEquity ?? matchedEquityRange.max;

        if (minValue === undefined || maxValue === undefined) {
            return undefined;
        }

        return {
            minEquity: toPercent(minValue),
            maxEquity: toPercent(maxValue)
        };
    };

    const resolvedRiskLevels = riskLevels && riskLevels.length > 0 ? [...riskLevels] : Object.keys(minMaxEquity || {});

    if (resolvedRiskLevels.length > 0) {
        return [...resolvedRiskLevels].reverse().map((riskLevel) => {
            const descriptionValues = getMatchedEquityRange(riskLevel);
            // Convert camelCase to UPPERCASE to map as keys in translation files
            const translationKey = riskLevel.toUpperCase();

            const dynamicDescription = descriptionValues
                ? t('DYNAMIC_RISK_TOLERANCE_HELPER', descriptionValues)
                : t(`RISK_TOLERANCE_HELPER_${translationKey}`);

            return {
                id: riskLevel,
                title: t(translationKey),
                description: dynamicDescription
            };
        });
    }

    return [
        {
            id: 'Aggressive',
            title: t('AGGRESSIVE'),
            description: t('RISK_TOLERANCE_HELPER_AGGRESSIVE')
        },
        {
            id: 'Moderate',
            title: t('MODERATE'),
            description: t('RISK_TOLERANCE_HELPER_MODERATE')
        },
        {
            id: 'Conservative',
            title: t('CONSERVATIVE'),
            description: t('RISK_TOLERANCE_HELPER_CONSERVATIVE')
        }
    ];
};

export const goalCategoryOptions = (t: any): OptionType<string | undefined>[] => [
    {
        label: t('SHORT'),
        value: 'Short Term'
    },
    {
        label: t('MEDIUM'),
        value: 'Medium Term'
    },
    {
        label: t('LONG'),
        value: 'Long Term'
    }
];

export const getTypeOptions = (t: any): OptionType<string | undefined>[] => [
    {
        value: undefined,
        label: t('ALL_TEXT')
    },
    {
        value: 'Wealth',
        label: t('LABEL_WEALTH')
    },
    {
        value: 'Retirement',
        label: t('RETIREMENT')
    }
];

export const typeOptions: OptionType<string | undefined>[] = [
    {
        value: undefined,
        label: 'All'
    },
    {
        value: 'WEALTH_GOAL',
        label: 'Wealth'
    },
    {
        value: 'retirement',
        label: 'Retirement'
    }
];

export const getPriorityOptions = (t: any, tenant?: string): OptionType<string | undefined>[] => [
    {
        value: undefined,
        label: t('ALL_TEXT')
    },
    {
        value: 'need',
        label: t('NEED')
    },
    {
        value: 'want',
        label: t('WANT')
    },
    {
        value: 'wish',
        label: tenant === 'banortemex' ? t('ASPIRE') : t('WISH')
    },
    {
        value: 'dream',
        label: t('DREAM')
    }
];

export const priorityOptions: OptionType<string | undefined>[] = [
    {
        value: undefined,
        label: 'All'
    },
    {
        value: 'need',
        label: 'Need'
    },
    {
        value: 'want',
        label: 'Want'
    },
    {
        value: 'wish',
        label: 'Wish'
    },
    {
        value: 'dream',
        label: 'Dream'
    }
];

export const admin_tenantList = [
    { label: 'DA Classic', value: 'advice' },
    { label: 'DA Evolve', value: 'daevolve' },
    { label: 'Hantz', value: 'hantz' },
    { label: 'Bangkok Bank', value: 'bangkokbank' },
    { label: 'Kasikorn Bank', value: 'kasikorn' },
    { label: 'Vesta Wealth', value: 'vestawealth' },
    { label: 'Banorte Mexico', value: 'banortemex' }
];

export const appFlowDictionary: Record<string, string[]> = {
    flow1: ['hantz'],
    flow2: ['banortemex', 'daevolve']
    // Add more flows and their associated tenants as needed
};

export const getCurrencyOptions = (t: any) => [
    {
        label: t('ENGLISH-US'),
        value: 'USD',
        currencyLocale: 'en-US'
    },
    {
        label: t('ENGLISH-CA'),
        value: 'CAD',
        currencyLocale: 'en-CA'
    },
    {
        label: t('ESPAÑOL-MXN'),
        value: 'MXN',
        currencyLocale: 'es-419'
    }
];
export const getAdminUserRoles = (t: any) => [
    {
        label: t('DA_ADMIN'),
        value: 'ftadmin',
        accessibleTo: ['ftadmin']
    },
    {
        label: t('TEXT_ADMIN_BUTTON'),
        value: 'admin',
        accessibleTo: ['ftadmin', 'admin']
    },
    {
        label: t('ADVISOR_TEXT'),
        value: 'advisor',
        accessibleTo: ['ftadmin', 'admin']
    }
];

export const DEFAULT_CURRENCY_SYMBOL = 'USD';
export const DEFAULT_CURRENCY_LOCALE = 'en-US';

// Number of monthly columns the Contribution Schedule spans per reallocation frequency.
export const REALLOC_FREQUENCY_COLUMN_COUNT: Record<string, number> = {
    yearly: 12,
    halfyearly: 6,
    quarterly: 3
};

export const API_VERSION = 'v1369';

export const allowedDomainNameOrigins = [
    'https://www.franklintempletonoffshore.com',
    'https://www.franklintempleton.com'
];

export const OKTA_LOGIN_URL = 'https://ftext.okta.com/oauth2/default';

export const FEATURE_FLAGS_RETRY_TIMES = 10;

export const REL_BANNER_VERSION = 'novRelease'; // hardcoded value for now, can be updated later

export const FEATURE_NAMES = {
    'RECOMMENDATION_RECONCILIATION_FE_AEX-1848': 'Recommendation_Reconciliation_FE_AEX-1848',
    'PLAN_DETAILS_ENHANCEMENT_FE_AEX-2554': 'Plan_Details_Enhancement_FE_AEX-2554',
    'DA_FLOW2_FE_AEX-2751_AEX-2626': 'DA_Flow2_FE-AEX-2751-AEX-2626',
    'TENANT_MULTI_SELECT_CMS_BE_FE_AEX-2805-2825': 'Tenant_Multi_Select_CMS_BE_FE_AEX-2805-2825',
    'TENANT_COMBO_MULTI_SELECT_CMS_FE_AEX-2805': 'Tenant_Combo_Multi_Select_CMS_FE_AEX-2805',
    'AEX-2821_NEW_GETGOECONFIG_ENDPOINT_INTEGRATION': 'AEX-2821_new_getgoeconfig_endpoint_integration',
    'GMAIL_IDS_RESTRICTION_FE_AEX-2879': 'Gmail_IDs_Restriction_FE_AEX-2879',
    'PROPOSALS_PAGINATION_SEARCH_FE_AEX-2382': 'Proposals_Pagination_Search_FE_AEX-2382',
    'BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021': 'Backend_Managed_ID_Timestamps_FE_AEX-3021',
    'AEX-3590_3604_NEW_FLOW_UPDATE_SAVE_PLAN': 'AEX-3590_3604_new_flow_update_save_plan',
    'ACCOUNT_INCOME_MAPPING_FE_AEX-3270': 'Account_Income_Mapping_FE_AEX-3270',
    'AEX-3245_PAGINATION_WITH_TIMESTAMP_SORTING': 'AEX-3245_pagination_with_timestamp_sorting',
    'CUSTOMER_ID_FEATURE_AEX-3360_AEX-3380': 'Customer_Id_Feature_AEX-3360_AEX-3380',
    'FUNDINGNAV_FULLSCHEDULE_FE_AEX-3368': 'FundingNav_FullSchedule_FE-AEX-3368',
    'PLANEXECUTION_FULLSCHEDULE_FE_AEX-3226': 'PlanExecution_FullSchedule_AEX-3226-AEX-3422',
    'GOALS_VIEW_REVAMP_FE_AEX-3428': 'Goals_View_Revamp_FE_AEX-3428',
    'NEW_TOOLTIP_FTDS_AEX-3428': 'New_Tooltip_FTDS_AEX-3428',
    'FUNDINGNAV_EWS_STREAMING_FE_AEX-3495': 'FundingNav_EWS_streaming_FE_AEX-3495',
    'FOOTER_REVAMP_FE_AEX-3479': 'Footer_Revamp_FE_AEX-3479',
    'SASH_REVAMP_FE_AEX-3480': 'Sash_Revamp_FE_AEX-3480',
    'PDF_OPTIMIZATION_AEX-3362': 'Pdf_optimization_AEX-3362',
    'AEX-2926_ADD_TENANT_PARAMETER_IN_UPDATE_PORTFOLIO': 'AEX-2926_add_tenant_parameter_in_update_portfolio',
    // Shared with the backend flag of the same name — gates the stale-plan (reference-ID mismatch)
    // On UI, detection + "Plan Has Changed" banner shown when an advisor edits a plan open in two tabs.
    'AEX-3342_ADD_VALIDATION_BEFORE_UPDATE': 'AEX-3342_add_validation_before_update',
    'DATEFIELD_MMYYYY_FE_AEX-3223': 'DateField_MMYYYY_FE_AEX-3223',
    'NOTIFICATION_POPUP_FE_AEX-3213': 'Notification_Popup_FE_AEX-3213',
    // Gates the Advisor Console "N Goals need attention" indicator (flow2/banorte only), sourced from
    // the notifications array now returned by getproposals. This is a BE flag.
    'AEX-3570_NOTIFICATIONS_IN_GETPROPOSALS': 'AEX-3570_notifications_in_getproposals',
    'CONTINUITY_OF_ADVICE_FE_AEX-3603': 'Continuity_Of_Advice_FE_AEX-3603',
    'AMOUNT_FORMAT_COMPACT_FE_AEX-3635': 'Amount_Format_Compact_FE_AEX-3635'
};

export const WeightTypeOptions = [
    { label: 'STRATEGIC_ONLY', value: 'strategic_only' },
    { label: 'BOTH_STRATEGIC_&_TACTICAL', value: 'both' }
];

export const WeightOptions = [
    { label: 'STRATEGIC_WEIGHTS', value: 'strategic' },
    { label: 'TACTICAL_WEIGHTS', value: 'tactical' }
];

export const fundFilledOptions = [
    { label: 'YES', value: 'Yes' },
    { label: 'NO', value: 'No' }
];

export const invalidDateText = 'Invalid Date';

export const PropertyOptions = [
    {
        label: 'DIGITAL_ADVISOR',
        value: 'Digital Advisor'
    }
];

export const OKTA_STATUS_URL =
    'https://help.okta.com/oie/en-us/content/topics/users-groups-profiles/usgp-end-user-states.htm';

export const PROFILE_DETAILS_MANDATORY_KEYS: (keyof ClientRowData)[] = [
    'name',
    'lastName',
    'dob',
    'retirementAge',
    'planningAge'
];

export const INCOME_SOURCE_MANDATORY_KEYS: (keyof IncomeSourcesType)[] = [
    'sourceOwner',
    'sourceType',
    'sourceName',
    'frequency',
    'endYear',
    'beginningYear',
    'amount'
];

export const ACCOUNT_MANDATORY_KEYS: (keyof InvestmentSourcesType)[] = [
    'accountHolder',
    'accountName',
    'accountType',
    'amount'
];

// EWS Duration constants
export enum TermTabType {
    // eslint-disable-next-line no-unused-vars
    SHORT = 0,
    // eslint-disable-next-line no-unused-vars
    MEDIUM = 1,
    // eslint-disable-next-line no-unused-vars
    LONG = 2
}

// AEX-3108: single-portfolio tenants (advice, bangkokbank) have no Short/Medium/Long terms; every
// term/duration/goalCategory lookup uses this key instead.
export const DEFAULT_GOAL_CATEGORY = 'default' as const;
export const DEFAULT_GOAL_CATEGORY_ORDER = 0;

export const EWS_DURATIONS = ['Short Term', 'Medium Term', 'Long Term'] as const;
export type EwsDuration = typeof EWS_DURATIONS[number] | typeof DEFAULT_GOAL_CATEGORY;
export type EwsDurationKey = 'shortTerm' | 'mediumTerm' | 'longTerm' | typeof DEFAULT_GOAL_CATEGORY;

export const EWS_DURATION_KEYS: Record<EwsDuration, EwsDurationKey> = {
    'Short Term': 'shortTerm',
    'Medium Term': 'mediumTerm',
    'Long Term': 'longTerm',
    [DEFAULT_GOAL_CATEGORY]: DEFAULT_GOAL_CATEGORY
};

export const EWS_DURATION_MAP: Record<EwsDurationKey, EwsDuration> = {
    shortTerm: 'Short Term',
    mediumTerm: 'Medium Term',
    longTerm: 'Long Term',
    [DEFAULT_GOAL_CATEGORY]: DEFAULT_GOAL_CATEGORY
};

/** Terms the tenant plans against: Short/Medium/Long for multiportfolio tenants, else just `default`. */
export const getEwsDurations = (isMultiPortfolioTenant: boolean): readonly EwsDuration[] =>
    isMultiPortfolioTenant ? EWS_DURATIONS : [DEFAULT_GOAL_CATEGORY];

export const getEwsDurationKeys = (isMultiPortfolioTenant: boolean): EwsDurationKey[] =>
    getEwsDurations(isMultiPortfolioTenant).map((duration) => EWS_DURATION_KEYS[duration]);

export const EWS_TERM_TAB_KEY_MAP: Record<TermTabType, EwsDurationKey> = {
    [TermTabType.SHORT]: 'shortTerm',
    [TermTabType.MEDIUM]: 'mediumTerm',
    [TermTabType.LONG]: 'longTerm'
};

export const TERM_LOCALIZATION: Record<any, string> = {
    [TermTabType.SHORT]: 'SHORT',
    [TermTabType.MEDIUM]: 'MEDIUM',
    [TermTabType.LONG]: 'LONG'
};

// AEX-3324: Goal tenure categories — mirror the backend `cfg_goal_categories` used by
// `/api/advice/generategoalcategory`. Boundaries are in YEARS with inclusivity flags; the backend
// computes the whole-month gap between start and target date and buckets it by these ranges. Used
// on the FE to lock a saved goal's target date to the range that preserves its category. Keep these
// values in sync with the backend config.
export interface GoalTenureCategory {
    /** Matches the persisted `goalCategory` value (see EWS_DURATIONS). */
    value: EwsDuration;
    minYears: number;
    /** Infinity for the open-ended (long-term) bucket. */
    maxYears: number;
    minInclusive: boolean;
    maxInclusive: boolean;
}

export const GOAL_TENURE_CATEGORIES: GoalTenureCategory[] = [
    { value: 'Short Term', minYears: 0, maxYears: 1, minInclusive: true, maxInclusive: false },
    { value: 'Medium Term', minYears: 1, maxYears: 3, minInclusive: true, maxInclusive: true },
    { value: 'Long Term', minYears: 3, maxYears: Infinity, minInclusive: false, maxInclusive: true }
];
export const GOAL_PRIORITY_ORDER = ['need', 'want', 'wish', 'dream', 'desire'];

// Canonical portfolio asset-class sort order for the primary high-level groupings.
// The numeric values are used to keep Equity before Fixed Income before Cash anywhere asset classes are sorted or displayed.
export const ASSET_CLASS_ORDER: Record<string, number> = { Equity: 0, 'Fixed Income': 1, Cash: 2 };

// List of tenants that enable users to input Customer ID through UI in the client profile.
// Add required tenant(s) to this list to enable the feature for them.
// Customer ID for missing tenants will be generated through BE and will not be editable through UI.
export const PARTICIPANT_ID_AVAILABLE_TENANTS = ['banortemex'];

// Maps the `pageName` of resource documents (from /api/advice/getresourcedocuments) to the
// translation key of the page label shown in the Resources page list. Order here is the display order.
export const RESOURCES_PAGE_NAME_TO_LABEL_KEY: Record<string, string> = {
    advisor_console: 'ADVISOR_CONSOLE',
    profile_finances: 'CLIENT_ONBOARDING_STEP1_FF',
    risk_tolerance: 'CLIENT_PROFILE_RISK_TOLERANCE_HEADING',
    goals: 'CLIENT_ONBOARDING_STEP2',
    funding_navigator: 'CLIENT_ONBOARDING_STEP3',
    plan_overview: 'CLIENT_ONBOARDING_STEP4',
    plan_execution: 'CLIENT_ONBOARDING_STEP6'
};
